<template>
    <div>
        <template v-if="$device.small">
            <div
                uk-grid
                class="uk-flex uk-flex-middle"
            >
                <div>
                    <select-field
                        v-model="sortConfig.column"
                        :hide-label="true"
                        placeholder="Sort By"
                        :options="sortColumns"
                        @onInputChange="updateSort(null)"
                    />
                </div>
                <div>
                    <input
                        id="filter-asc"
                        v-model="sortConfig.direction"
                        type="radio"
                        value="asc"
                        @change="updateSort(null)"
                    >
                    <label for="filter-asc">ASC</label>
                </div>
                <div>
                    <input
                        id="filter-desc"
                        v-model="sortConfig.direction"
                        type="radio"
                        value="desc"
                        @change="updateSort(null)"
                    >
                    <label for="filter-desc">DESC</label>
                </div>
            </div>
            <hr>
            <ul
                uk-accordion
                class="uk-margin-large-bottom"
            >
                <payment-list-item
                    v-for="payment in payments"
                    :key="payment.id"
                    :payment="payment"
                    :timezone="timezone"
                    @edit="edit"
                    @getReceipt="getReceipt"
                    @changeStatus="changeStatus"
                    @resendPaymentLink="resendPaymentLink"
                />
            </ul>
        </template>
        <template v-else>
            <div class="uk-overflow-auto">
                <table class="uk-table uk-table-divider uk-table-hover uk-table-small uk-text-small">
                    <tr class="header-row">
                        <th class="uk-visible-toggle th-date th-lg">
                            Date
                            <sort-buttons
                                :class="getSortButtonClass('date')"
                                :highlight-direction="isColumnUsedForSorting('date')"
                                column="date"
                                @updateSort="updateSort"
                            />
                        </th>
                        <th class="uk-visible-toggle th-type">
                            Type
                            <sort-buttons
                                :class="getSortButtonClass('paymentType')"
                                :highlight-direction="isColumnUsedForSorting('paymentType')"
                                column="paymentType"
                                @updateSort="updateSort"
                            />
                        </th>
                        <th
                            v-if="isUserAdmin"
                            class="uk-visible-toggle th-type"
                        >
                            State
                            <sort-buttons
                                :class="getSortButtonClass('paymentState')"
                                :highlight-direction="isColumnUsedForSorting('paymentState')"
                                column="paymentState"
                                @updateSort="updateSort"
                            />
                        </th>
                        <th class="uk-visible-toggle th-id">
                            Job ID
                            <sort-buttons
                                :class="getSortButtonClass('jobNumber')"
                                :highlight-direction="isColumnUsedForSorting('jobNumber')"
                                column="jobNumber"
                                @updateSort="updateSort"
                            />
                        </th>
                        <th class="uk-visible-toggle th-amount">
                            Amount
                            <sort-buttons
                                :class="getSortButtonClass('amount')"
                                :highlight-direction="isColumnUsedForSorting('amount')"
                                column="amount"
                                @updateSort="updateSort"
                            />
                        </th>
                        <th class="uk-visible-toggle th-payer">
                            Paid By
                            <sort-buttons
                                :class="getSortButtonClass('paidBy')"
                                :highlight-direction="isColumnUsedForSorting('paidBy')"
                                column="paidBy"
                                @updateSort="updateSort"
                            />
                        </th>
                        <th class="uk-visible-toggle th-method">
                            Method
                            <sort-buttons
                                :class="getSortButtonClass('paymentMethod')"
                                :highlight-direction="isColumnUsedForSorting('paymentMethod')"
                                column="paymentMethod"
                                @updateSort="updateSort"
                            />
                        </th>
                        <th class="uk-visible-toggle th-status th-lg">
                            Payment Status
                            <sort-buttons
                                :class="getSortButtonClass('paymentStatus')"
                                :highlight-direction="isColumnUsedForSorting('paymentStatus')"
                                column="paymentStatus"
                                @updateSort="updateSort"
                            />
                        </th>
                        <th class="uk-visible-toggle th-receiver">
                            Received By
                            <sort-buttons
                                :class="getSortButtonClass('receivedBy')"
                                :highlight-direction="isColumnUsedForSorting('receivedBy')"
                                column="receivedBy"
                                @updateSort="updateSort"
                            />
                        </th>
                        <th class="th-buttons th-lg btn-header" />
                        <th class="th-actions deposit-header">
                            <button
                                type="button"
                                class="uk-button uk-button-small uk-button-primary"
                                :disabled="!hasPaymentToDeposit"
                                uk-tooltip="Print a list of cash and cheques to take to the bank."
                                @click="showDepositListModal"
                            >
                                Deposit list
                            </button>
                        </th>
                    </tr>
                    <payment-list-item
                        v-for="payment in payments"
                        :key="payment.id"
                        :payment="payment"
                        :timezone="timezone"
                        @edit="edit"
                        @getReceipt="getReceipt"
                        @changeStatus="changeStatus"
                        @resendPaymentLink="resendPaymentLink"
                    />
                </table>
            </div>
        </template>
    </div>
</template>

<script>

import { mapGetters } from 'vuex';
const PaymentListItem = () => import("./PaymentListItem");
const SelectField = () => import("@strangeanimals/common-vue-components/src/components/fields/SelectField");
const SortButtons = () => import("@strangeanimals/common-vue-components/src/components/list/SortButtons");

export default {
    components: { SelectField, PaymentListItem, SortButtons },
    props: {
        payments: {
            type: Array,
            default: function () {
                return [];
            }
        },
        depositList: {
            type: Array,
            default: function () {
                return [];
            }
        },
        filters: {
            type: Object,
            default: function () {
                return {};
            }
        },
        timezone: {
            type: String,
            default: 'local'
        }
    },
    data() {
        return {
            selectedFilters: {
                receivedBy: [],
                paymentTypes: [],
                paymentMethods: [],
                states: [],
                statuses: []
            },
            sortConfig: {
                column: null,
                direction: 'desc'
            },
            sortColumns: {
                date: 'Date',
                paymentType: 'Payment Type',
                paymentState: 'Payment State',
                jobNumber: 'Job Number',
                amount: 'Amount',
                paidBy: 'Paid By',
                paymentMethod: 'Payment Method',
                paymentStatus: 'Payment Status',
                receivedBy: 'Received By'
            }
        };
    },
    computed: {
        ...mapGetters({
            isUserAdmin: 'isUserAdmin'
        }),
        hasPaymentToDeposit() {
            return (this.depositList.length > 0);
        }
    },
    methods: {
        edit(paymentID) {
            return this.$emit('edit', paymentID)
        },
        getReceipt(paymentID) {
            return this.$emit('getReceipt', paymentID)
        },
        resendPaymentLink(paymentToken) {
            return this.$emit('resendPaymentLink', paymentToken)
        },
        showDepositListModal() {
            this.$uikit.modal('#deposit-list-modal').show()
        },
        updateSort(data = null) {
            if (data) {
                this.sortConfig = data.sortConfig;
            }
            this.$emit('updateSort', {sortConfig: this.sortConfig});
        },
        getSortButtonClass(column) {
            return { 'uk-invisible-hover': !this.isColumnUsedForSorting(column) };
        },
        isColumnUsedForSorting(column) {
            return this.sortConfig.column === column;
        },
        changeStatus(data) {
            this.$emit('changeStatus', data);
        }
    }
}
</script>
